import { useTheme } from "@modernatx-internal/component-library";
import React from "react";

import { AdvancedMarker } from "@/finder/googleMaps/AdvancedMarker";
import { useFinder } from "@/finder/hooks/V2/useFinder";
import type { FinderLocation } from "@/types/FinderLocation";

import { Map } from "./Map";
import MapViewController from "./MapViewController";
import { Marker } from "./Marker";
import { SearchThisArea } from "./SearchThisArea";

export const SearchLocationMarker = React.forwardRef<HTMLDivElement>((_, ref) => {
  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(226,24,55, 0.4);
            }
            30% {
              box-shadow: 0 0 0 5px rgba(226,24,55, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(226,24,55, 0);
            }
          }
        `}
      </style>
      <div
        ref={ref}
        style={{
          position: "relative",
          width: "15px",
          height: "15px",
          backgroundColor: "rgb(226,24,55)",
          border: "2px solid white",
          borderRadius: "50%",
          boxShadow: "0 0 0 rgba(226,24,55, 0.7)",
          animation: "pulse 5s infinite"
        }}
      />
    </>
  );
});

SearchLocationMarker.displayName = "SearchLocationMarker";

const FinderMap: React.FC<{ isSearchAreaEnabled?: boolean }> = ({ isSearchAreaEnabled = true }) => {
  const {
    country,
    locationHovered,
    locationSelected,
    locationSelectedSet,
    place,
    resultsView,
    visibleSearchResults
  } = useFinder();
  const { zIndices } = useTheme();
  // Detect if device supports hover (non-touch devices)
  const [supportsHover, setSupportsHover] = React.useState(false);

  React.useEffect(() => {
    // Check if device supports hover capability using CSS media query
    const mediaQuery = window.matchMedia("(hover: hover)");
    setSupportsHover(mediaQuery.matches);

    // Listen for changes (e.g., when connecting/disconnecting mouse)
    const handleChange = (e: MediaQueryListEvent) => {
      setSupportsHover(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const isSelectedLocation = (location?: FinderLocation) => {
    return locationSelected?.customer.id === location?.customer.id;
  };
  const isHoveredLocation = (location?: FinderLocation) => {
    return locationHovered?.customer.id === location?.customer.id;
  };

  const hidden = resultsView === "mobileList";

  return (
    <Map hidden={hidden}>
      {isSearchAreaEnabled && <SearchThisArea resultsView={resultsView} />}
      <MapViewController hidden={hidden} />
      {visibleSearchResults?.map((searchResult) => {
        // "as" is neccesary here to convert theme.zIndicies to number
        // because when creating google maps AdvancedMarkerElements, the zIndex type must be a number.
        const zIndex: number | null = isHoveredLocation(searchResult)
          ? (zIndices.dropdown as number)
          : isSelectedLocation(searchResult)
            ? (zIndices.navbar as number)
            : null;
        return (
          <AdvancedMarker
            key={searchResult.customer.id}
            position={{
              lat: searchResult.location.position.lat,
              lng: searchResult.location.position.lon
            }}
            zIndex={zIndex}
            onClick={() => locationSelectedSet(searchResult)}
          >
            <Marker location={searchResult} supportsHover={supportsHover} />
          </AdvancedMarker>
        );
      })}
      {place && (
        <AdvancedMarker
          position={{
            lat: place.latitude,
            lng: place.longitude
          }}
          zIndex={null}
        >
          {country !== "jp" && <SearchLocationMarker />}
        </AdvancedMarker>
      )}
    </Map>
  );
};

export default FinderMap;
